import {
  createProductPart,
  deleteProductPart,
  fetchProductParts,
  fetchProducts,
  updateProductPart,
} from '../../api/product';
import { PRODUCT_UNITS, Product, ProductPart, ProductPartPayload, formatWon } from '../../types/product';
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import '../equipment/EquipTabPanel.css';

import { reportError } from '../../utils/notify';
import { formatNumberDisplay, formatNumberInput, parseNumberInput } from '../../utils/numberFormat';
interface Props {
  productId: number;
}

type FormState = {
  mode: 'link' | 'create';
  childProductId: number | '';
  partName: string;
  spec: string;
  unit: string;
  qty: number | '';
  unitPrice: number | '';
  remark: string;
};

const EMPTY: FormState = {
  mode: 'create',
  childProductId: '',
  partName: '',
  spec: '',
  unit: 'EA',
  qty: 1,
  unitPrice: '',
  remark: '',
};

function parseNonNegNumber(raw: string): number | '' {
  if (raw === '') return '';
  const n = parseNumberInput(formatNumberInput(raw));
  if (n == null) return '';
  return Math.max(0, n);
}

function ProductPartsTab({ productId }: Props) {
  const [rows, setRows] = useState<ProductPart[]>([]);
  const [catalog, setCatalog] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [parts, products] = await Promise.all([
        fetchProductParts(productId),
        fetchProducts(),
      ]);
      setRows(parts);
      setCatalog(products.filter((item) => item.productId !== productId));
      setError('');
    } catch {
      reportError(setError, '구성 부품을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    void load();
  }, [load]);

  const linkedChildIds = useMemo(
    () => new Set(rows.map((row) => row.childProductId).filter(Boolean) as number[]),
    [rows]
  );

  const availableCatalog = catalog.filter(
    (item) => editingId != null || !linkedChildIds.has(item.productId)
  );

  const totalAmount = rows.reduce((sum, row) => sum + (row.amount ?? 0), 0);

  const openCreate = () => {
    setEditingId(null);
    setForm({
      ...EMPTY,
      mode: availableCatalog.length > 0 ? 'link' : 'create',
    });
    setShowForm(true);
  };

  const openEdit = (row: ProductPart) => {
    setEditingId(row.partId);
    setForm({
      mode: row.childProductId ? 'link' : 'create',
      childProductId: row.childProductId ?? '',
      partName: row.partName,
      spec: row.spec ?? '',
      unit: row.unit || 'EA',
      qty: row.qty ?? 1,
      unitPrice: row.unitPrice ?? '',
      remark: row.remark ?? '',
    });
    setShowForm(true);
  };

  const applyChildProduct = (childId: number | '') => {
    if (childId === '') {
      setForm((prev) => ({ ...prev, childProductId: '' }));
      return;
    }
    const child = catalog.find((item) => item.productId === childId);
    if (!child) {
      setForm((prev) => ({ ...prev, childProductId: childId }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      childProductId: childId,
      partName: child.productName,
      spec: child.spec ?? '',
      unit: child.unit || 'EA',
      unitPrice: child.costPrice ?? prev.unitPrice,
    }));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError('');
      if (editingId == null) {
        const payload: ProductPartPayload =
          form.mode === 'link'
            ? {
                childProductId: form.childProductId === '' ? null : Number(form.childProductId),
                partName: form.partName.trim() || undefined,
                spec: form.spec.trim() || undefined,
                unit: form.unit || 'EA',
                qty: Number(form.qty) || 0,
                unitPrice: Number(form.unitPrice) || 0,
                remark: form.remark.trim() || undefined,
              }
            : {
                partName: form.partName.trim(),
                spec: form.spec.trim() || undefined,
                unit: form.unit || 'EA',
                qty: Number(form.qty) || 0,
                unitPrice: Number(form.unitPrice) || 0,
                remark: form.remark.trim() || undefined,
              };
        if (form.mode === 'link' && !payload.childProductId) {
          reportError(setError, '연결할 제품을 선택해 주세요.');
          return;
        }
        if (form.mode === 'create' && !payload.partName) {
          reportError(setError, '품명을 입력해 주세요.');
          return;
        }
        await createProductPart(productId, payload);
      } else {
        await updateProductPart(productId, editingId, {
          partName: form.partName.trim(),
          spec: form.spec.trim() || undefined,
          unit: form.unit || 'EA',
          qty: Number(form.qty) || 0,
          unitPrice: Number(form.unitPrice) || 0,
          remark: form.remark.trim() || undefined,
        });
      }
      setShowForm(false);
      await load();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '저장에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: ProductPart) => {
    if (!window.confirm(`'${row.partName}'을(를) 구성 부품에서 제거할까요?`)) return;
    try {
      await deleteProductPart(productId, row.partId);
      await load();
    } catch {
      reportError(setError, '삭제에 실패했습니다.');
    }
  };

  return (
    <div className="equip-tab-panel">
      <div className="equip-tab-panel__toolbar">
        <div>
          <h3>부품(BOM)</h3>
          <p className="equip-tab-panel__hint">
            이 제품을 구성하는 부품·수량·단가를 등록합니다. 제품 마스터에서 고르거나 직접 입력할 수
            있습니다.
          </p>
        </div>
        <button type="button" onClick={openCreate}>
          추가
        </button>
      </div>

      {error && <p className="equip-tab-panel__error">{error}</p>}

      {showForm && (
        <form className="equip-tab-panel__form" onSubmit={(e) => void handleSubmit(e)}>
          {editingId == null && (
            <label className="is-span-2">
              등록 방식
              <select
                value={form.mode}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    mode: e.target.value as 'link' | 'create',
                    childProductId: '',
                  }))
                }
              >
                <option value="link">제품 목록에서 선택</option>
                <option value="create">직접 입력</option>
              </select>
            </label>
          )}

          {editingId == null && form.mode === 'link' ? (
            <label className="is-span-2">
              구성 제품 *
              <select
                value={form.childProductId}
                onChange={(e) =>
                  applyChildProduct(e.target.value ? Number(e.target.value) : '')
                }
                required
              >
                <option value="">선택</option>
                {availableCatalog.map((item) => (
                  <option key={item.productId} value={item.productId}>
                    {item.productName}
                    {item.productCode ? ` (${item.productCode})` : ''}
                    {item.category ? ` · ${item.category}` : ''}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <label>
            품명 *
            <input
              value={form.partName}
              onChange={(e) => setForm((prev) => ({ ...prev, partName: e.target.value }))}
              required
              placeholder="예: 메인보드"
              maxLength={255}
            />
          </label>
          <label>
            규격
            <input
              value={form.spec}
              onChange={(e) => setForm((prev) => ({ ...prev, spec: e.target.value }))}
              placeholder="예: 120×80mm"
              maxLength={255}
            />
          </label>
          <label>
            단위
            <select
              value={form.unit}
              onChange={(e) => setForm((prev) => ({ ...prev, unit: e.target.value }))}
            >
              {PRODUCT_UNITS.map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </select>
          </label>
          <label>
            수량 *
            <input
              type="text"
              inputMode="decimal"
              value={form.qty === '' ? '' : formatNumberDisplay(form.qty)}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, qty: parseNonNegNumber(e.target.value) }))
              }
              required
            />
          </label>
          <label>
            단가(원)
            <input
              type="text"
              inputMode="decimal"
              value={form.unitPrice === '' ? '' : formatNumberDisplay(form.unitPrice)}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, unitPrice: parseNonNegNumber(e.target.value) }))
              }
            />
          </label>
          <label>
            금액
            <input
              value={formatWon((Number(form.qty) || 0) * (Number(form.unitPrice) || 0))}
              readOnly
            />
          </label>
          <label className="is-span-2">
            비고
            <input
              value={form.remark}
              onChange={(e) => setForm((prev) => ({ ...prev, remark: e.target.value }))}
              maxLength={500}
              placeholder="대체품, 공급처 메모 등"
            />
          </label>
          <div className="equip-tab-panel__form-actions">
            <button type="button" className="is-ghost" onClick={() => setShowForm(false)}>
              취소
            </button>
            <button type="submit" disabled={saving}>
              {saving ? '저장 중...' : '저장'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="equip-tab-panel__hint">불러오는 중...</p>
      ) : rows.length === 0 ? (
        <p className="equip-tab-panel__hint">구성 부품을 추가해 주세요.</p>
      ) : (
        <>
          <table className="equip-tab-panel__table">
            <thead>
              <tr>
                <th style={{ width: '48px' }}>No</th>
                <th>품명</th>
                <th>규격</th>
                <th style={{ width: '64px' }}>단위</th>
                <th style={{ width: '72px' }}>수량</th>
                <th style={{ width: '100px' }}>단가</th>
                <th style={{ width: '110px' }}>금액</th>
                <th>비고</th>
                <th style={{ width: '120px' }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.partId}>
                  <td>{index + 1}</td>
                  <td>{row.partName}</td>
                  <td>{row.spec || '-'}</td>
                  <td>{row.unit || 'EA'}</td>
                  <td>{row.qty ?? 0}</td>
                  <td>{formatWon(row.unitPrice)}</td>
                  <td>{formatWon(row.amount)}</td>
                  <td>{row.remark || '-'}</td>
                  <td>
                    <div className="equip-tab-panel__row-actions">
                      <button type="button" className="is-ghost" onClick={() => openEdit(row)}>
                        수정
                      </button>
                      <button
                        type="button"
                        className="is-danger"
                        onClick={() => void handleDelete(row)}
                      >
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="equip-tab-panel__hint" style={{ textAlign: 'right', margin: 0 }}>
            부품 합계 {formatWon(totalAmount)}
          </p>
        </>
      )}
    </div>
  );
}

export default ProductPartsTab;
